import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import { analytics } from 'Modules/Analytics'
import { initiatives } from 'Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { Box, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Button, Snackbar } from '@resiliatech/birdsong'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import moment, { Moment } from 'moment/moment'
import {
  EntryInput,
  Goal,
  GoalUpdateInput,
  Dimension,
  ChartSeriesDataPoint,
} from 'Generated/graphql-types'
import LegendToggleOutlinedIcon from '@mui/icons-material/LegendToggleOutlined'
import { useMutation, useQuery } from '@apollo/client'
import CREATE_ENTRY from 'Apollo/Mutations/CreateEntry'
import UPDATE_GOAL from 'Apollo/Mutations/UpdateGoal'
import { UPDATE_CHART } from 'Apollo/Mutations/UpdateChart'
import { GOALS } from 'Apollo/Queries/Goals'
import { useAppState } from 'Context/AppContext/AppContext'
import CreateChartWizard from 'Components/Charts/MultiLineChart/CreateChartWizard'
import { CreateChartButton } from 'Components/Charts/MultiLineChart/CreateChartButton'
import GET_DIMENSIONS from 'Apollo/Queries/GetDimensions'
import { CHART } from 'Apollo/Queries/Chart'
import {
  Actions,
  ChartContainer,
  DuplicateDateChip,
  Header,
  HeaderContainer,
  ProgressChartContainer,
  Spacer,
  ChartTitle,
} from './ProgressChart.styled'
import { ChartTypeSelector } from '../ChartTypeSelector/ChartTypeSelector'
import { EditChartModal } from '../EditChartModal/EditChartModal'
import { AddChartDataModal } from '../AddChartDataModal/AddChartDataModal'
import {
  Chart,
  ChartCalculationOptionsEnum,
  ChartTypeOptionsEnum,
} from '../types'
import { GoalChart } from '../GoalChart/GoalChart'
import {
  getChartSeriesDataFromEntryGroup,
  MM_DD_YY,
  switchDataVisualization,
} from '../utils'
import { ChartDataTable } from '../ChartDataTable/ChartDataTable'
import { GoalSectionEmptyState } from '../EmptyStates/GoalSectionEmptyState'
import { ChartMenu } from '../ChartMenu/ChartMenu'

type ProgressChartProps = {
  goal: Goal
  isGoalSharedWithFunder: boolean
  goalIsSharedByFunder?: boolean
  simple?: boolean
  chartType?: ChartTypeOptionsEnum
  customChartData?: any // Custom chart data with filtered categories
}

export const ProgressChart = ({
  goal,
  isGoalSharedWithFunder,
  goalIsSharedByFunder,
  simple,
  chartType,
  customChartData,
}: ProgressChartProps) => {
  const {
    nonprofitOrgConfig: { orgId: nonprofitOrgId, role, nonprofitOrgRole },
    funderOrgConfig: { orgId: funderOrgId },
  } = useAppState()
  const [newEntryDate, setNewEntryDate] = useState<Moment | null>(
    moment().utc()
  )
  const [isAddingData, setIsAddingData] = useState(false)
  const [isCreatingChart, setIsCreatingChart] = useState<boolean>(false)
  const [isEditingChart, setIsEditingChart] = useState(false)
  const [isEditingData, setIsEditingData] = useState(false)
  const [localChartType, setLocalChartType] = useState<ChartTypeOptionsEnum>(
    chartType || ChartTypeOptionsEnum.line
  )
  const [chartMenuOpen, setChartMenuOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [showSnackbar, setShowSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')

  const popToast = (message: string) => {
    setSnackbarMessage(message)
    setShowSnackbar(true)
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setChartMenuOpen(true)
    setMenuAnchor(event.currentTarget)
  }

  const { data: chartDisplayData, loading: chartDataLoading } = useQuery(
    CHART,
    {
      variables: { goalId: goal.id },
    }
  )

  const [chartData, setChartData] = useState<Chart | null>(
    chartDisplayData?.chart ?? null
  )

  useEffect(() => {
    // If custom chart data is provided, use that instead of the query data
    if (customChartData) {
      // Respect the chart's original calculation type
      const calculationType =
        customChartData.calculationType === 'INCREMENTAL'
          ? ChartCalculationOptionsEnum.incremental
          : ChartCalculationOptionsEnum.cumulative

      const seriesData = switchDataVisualization(
        customChartData.seriesData,
        calculationType
      )

      setChartData({
        ...customChartData,
        seriesData,
        calculationType,
      })
    } else if (chartDisplayData?.chart) {
      const { chart } = chartDisplayData

      // Respect the chart's original calculation type
      const calculationType =
        chart.calculationType === 'INCREMENTAL'
          ? ChartCalculationOptionsEnum.incremental
          : ChartCalculationOptionsEnum.cumulative

      const seriesData = switchDataVisualization(
        chart.seriesData,
        calculationType
      )

      setChartData({
        ...chart,
        seriesData,
        calculationType,
      })
    }
  }, [chartDisplayData, customChartData])

  // Update localChartType when chartType prop changes
  useEffect(() => {
    if (chartType) {
      setLocalChartType(chartType)
    }
  }, [chartType])

  const [createEntry] = useMutation(CREATE_ENTRY, {
    refetchQueries: [
      {
        query: GOALS,
        variables: { activeOnly: true, goalId: goal.id, nonprofitOrgId },
      },
    ],
    awaitRefetchQueries: true,
  })

  const [updateGoal] = useMutation(UPDATE_GOAL, {
    refetchQueries: [
      { query: GOALS, variables: { activeOnly: true, nonprofitOrgId } },
    ],
  })

  const [archiveChart] = useMutation(UPDATE_CHART, {
    refetchQueries: [
      {
        query: GOALS,
        variables: { activeOnly: true, nonprofitOrgId, goalId: `${goal.id}` },
      },
      { query: CHART, variables: { goalId: goal.id } },
    ],
    awaitRefetchQueries: true,
  })

  const handleEditChart = async (goalUpdateInfo: GoalUpdateInput) => {
    await updateGoal({ variables: { goalUpdateInfo } })
    setIsEditingChart(false)
  }

  const handleDeleteChart = async () => {
    if (chartDisplayData) {
      await archiveChart({
        variables: {
          data: {
            chartId: chartDisplayData.chart.id,
            shouldArchive: true,
            target: chartDisplayData.chart.target,
            title: chartDisplayData.chart.title,
            calculationType: chartDisplayData.chart.calculationType,
            unitOfMeasurement: chartDisplayData.chart.unitOfMeasurement,
          },
        },
      })
      setChartData(null)
      popToast('Your chart and data was deleted')
    }
  }

  const { data: { getDimensions: dimensions = [] } = {} } = useQuery(
    GET_DIMENSIONS,
    {
      variables: { nonprofitOrgId },
    }
  )

  const handleAddData = (dateToAdd: string, entries: EntryInput[]) => {
    createEntry({
      variables: {
        nonprofitOrgId,
        entryGroupInfo: {
          goalId: goal.id,
          reportedAt: new Date(dateToAdd).toISOString(),
        },
        entryInfo: entries,
      },
      refetchQueries: [GOALS],
    })
    setIsAddingData(false)
  }

  const series = getChartSeriesDataFromEntryGroup(
    goal.EntryGroup,
    goal.GoalToDimension
  )

  const categories = series.map(({ goalToDimensionId, label }) => ({
    goalToDimensionId,
    label,
  }))
  const enablePieChartOption =
    (chartData && chartData.seriesData.length > 1) || false
  const hasSeriesData = chartData && chartData.seriesData.length > 0
  const hasDataPoints = series.some((s) => s.data.length > 0)
  const hasChart = !!chartData
  const chartId = chartDisplayData?.chart?.id

  useEffect(() => {
    if (!hasDataPoints) setIsEditingData(false)
  }, [hasDataPoints])

  const isNewEntryDateDuplicate = !!(
    newEntryDate &&
    isAddingData &&
    chartDisplayData?.chart?.seriesData[0]?.data.some(
      (point: ChartSeriesDataPoint) =>
        moment.utc(point.x).format(MM_DD_YY) === newEntryDate?.format(MM_DD_YY)
    )
  )

  const handleAddDataClick = () => {
    setIsAddingData(true)
    analytics.trackActionCreator(initiatives.Create_Data_Point_Clicked)({
      role,
      relationship: nonprofitOrgRole,
      goalId: goal.id,
    })
  }

  const hasNoData = chartData?.seriesData.every((s) => s.data.length === 0)

  return (
    <ProgressChartContainer simple={simple}>
      {!simple && (
        <HeaderContainer>
          <Header>Progress Chart</Header>
          <Actions>
            {!chartDataLoading && !chartId && !funderOrgId && (
              <CreateChartButton
                goalId={goal.id}
                goalTitle={goal.title}
                onOpen={setIsCreatingChart}
              />
            )}
          </Actions>
        </HeaderContainer>
      )}
      {!hasChart && (
        <GoalSectionEmptyState
          simple={simple}
          icon={<LegendToggleOutlinedIcon />}
          message='Add data to create a chart and visualize your progress'
        />
      )}
      {hasSeriesData && (
        <ChartContainer simple={simple}>
          {!simple && <ChartTitle>{chartData?.title}</ChartTitle>}
          {!simple && !hasNoData && (
            <ChartTypeSelector
              chartType={localChartType}
              onChange={setLocalChartType}
              enablePieChartOption={enablePieChartOption}
            />
          )}
          <Spacer />
          {hasSeriesData && (
            <GoalChart
              height={simple ? 370 : 500}
              chart={chartDisplayData?.chart}
              chartType={chartType || localChartType}
              goal={goal}
              pieChartSeriesData={chartDisplayData?.chart?.seriesData}
              series={chartData ? chartData?.seriesData : series}
            />
          )}
        </ChartContainer>
      )}
      {chartId && (
        <>
          <Actions shouldSpaceBetween={isNewEntryDateDuplicate}>
            {isNewEntryDateDuplicate && (
              <DuplicateDateChip>
                <ReportProblemOutlinedIcon />
                <Box width='max-content'>
                  You have already added data for{' '}
                  {newEntryDate.format('MM/DD/YY')}.
                </Box>
              </DuplicateDateChip>
            )}
            {!simple && !funderOrgId && (
              <Actions>
                {!goalIsSharedByFunder && (
                  <Button
                    variant='tertiary'
                    label='Edit Chart'
                    onClick={() => setIsEditingChart(true)}
                  />
                )}
                <Button
                  variant='primary'
                  label='Add Data'
                  onClick={handleAddDataClick}
                />
                {chartId && !goalIsSharedByFunder && (
                  <ChartMenu
                    chartMenuOpen={chartMenuOpen}
                    onClose={() => setChartMenuOpen(false)}
                    onClick={handleMenuClick}
                    menuAnchor={menuAnchor}
                    onDeleteChart={handleDeleteChart}
                  />
                )}
              </Actions>
            )}
          </Actions>
          {!simple && !funderOrgId && (
            <ChartDataTable
              goal={goal}
              isAddingData={isAddingData}
              newEntryDate={newEntryDate}
              onAddData={() => setIsAddingData(false)}
              onChangeNewEntryDate={setNewEntryDate}
              isGoalSharedWithFunder={isGoalSharedWithFunder}
              onEmptyStateAddData={handleAddDataClick}
            />
          )}
        </>
      )}
      {!chartId && (
        <CreateChartWizard
          allDimensions={_.orderBy(dimensions as Dimension[], 'title', 'asc')}
          goalId={goal.id}
          goalTitle={goal.title}
          isOpen={isCreatingChart}
          onToggle={setIsCreatingChart}
          isGoalSharedWithFunder={isGoalSharedWithFunder}
        />
      )}
      <AddChartDataModal
        categories={categories}
        isOpen={isAddingData}
        onAddData={handleAddData}
        onClose={() => setIsAddingData(false)}
      />
      {isEditingChart && (
        <EditChartModal
          goal={goal}
          isOpen={isEditingChart}
          onClose={() => setIsEditingChart(false)}
          onEditChart={handleEditChart}
          chart={chartDisplayData.chart}
        />
      )}
      <Snackbar
        open={showSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        message={snackbarMessage}
        autoHideDuration={2000}
        onClose={() => setShowSnackbar(false)}
        action={
          <IconButton
            aria-label='close'
            color='inherit'
            sx={{ p: 0.5 }}
            onClick={() => setShowSnackbar(false)}
          >
            <CloseIcon />
          </IconButton>
        }
      />
    </ProgressChartContainer>
  )
}
